// IMPORT START
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { createServiceGroup } from "../../../../actions/serviceGroupActions";
import { backgroundLight, primaryAlt } from "../../../../utils/colors";
import {
  Box,
  Heading,
  Flex,
  Button,
  Tooltip,
  Icon,
  Spinner,
  IconButton,
  Text,
} from "@chakra-ui/react";
import {
  primary,
  white,
  textSecondary,
  buttonRed,
  buttonRedText,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import FormControlDefault from "../../../../components/input/FormControlDefault";
import { SERVICEGROUP_CREATE_RESET } from "../../../../constants/serviceGroupConstants";
import { motion } from "framer-motion";
import { FaArrowLeftLong, FaCircleInfo, FaXmark } from "react-icons/fa6";
import useThemeColors from "../../../../hooks/useThemeColors";
import StatusButtonNew from "../../../../components/buttons/StatusButtonNew";
import { useParams } from "react-router-dom";
import useIsMobile from "../../../../hooks/useIsMobile";

const CreateServiceGroup = ({ close }) => {
  // GENERAL
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  //COLORS
  const { whiteMode, newBorderMode, textPrimaryMode, backgroundLightMode } =
    useThemeColors();

  // STATE
  const [loading, setLoading] = useState(false);
  const [loadingDisplay, setLoadingDisplay] = useState(true);

  // FORM
  const [customId, setCustomId] = useState("");
  const [name, setName] = useState("");
  const [isConsulting, setIsConsulting] = useState(false);
  const [treatmentStep, setTreatmentStep] = useState("");
  const [status, setStatus] = useState("active");

  // ERRORS
  const isNameError = name === "";

  // TOOLTIP VALIDATION MESSAGES
  const tooltipMessages = [];

  if (isNameError)
    tooltipMessages.push(t("formError.serviceGroupNameRequired"));

  // HANDLERS
  const closeHandler = () => {
    close();
    dispatch({ type: SERVICEGROUP_CREATE_RESET });
  };

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(createServiceGroup(slug, name, status));
  };

  // USE EFFECT
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingDisplay(false);
    }, 300); // CHANGE THIS TO HAVE A LONGER LOADING TIME

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeout);
  }, []);

  // LOGS

  // console.log("customId", customId);
  // console.log("name", name);
  // console.log("treatmentStep", treatmentStep);
  // console.log("isConsulting", isConsulting);

  return (
    <>
      {loading || loadingDisplay ? (
        <Flex bg={whiteMode} align="center" justify="center" w="100%" h="100%">
          <Spinner color={primary} size="lg" />
        </Flex>
      ) : (
        <Box
          position="relative"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          w="100%"
          h="100%"
        >
          <form
            onSubmit={submitHandler}
            style={{
              height: "100%",
            }}
          >
            <Flex w="100%" h="100%" direction="column">
              {/* HEADER START */}
              <Flex
                justify="space-between"
                align="center"
                p={["1.25rem 1rem 1.25rem", "1.25rem 2rem 1.25rem"]}
                borderBottom={`1px solid ${newBorderMode}`}
                mb="0rem"
                position="relative"
                zIndex={90}
                w="100%"
              >
                <Flex align="center">
                  {!isMobile && (
                    <IconButton
                      bg={backgroundLightMode}
                      border={`1px solid ${backgroundLightMode}`}
                      color={textSecondary}
                      icon={<FaArrowLeftLong />}
                      borderRadius="10.75rem"
                      fontSize="0.875rem"
                      onClick={closeHandler}
                      mr="1rem"
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: primaryAlt,
                      }}
                    />
                  )}

                  <Flex align="center">
                    <Heading
                      fontSize={["1rem", "1.125rem"]}
                      color={textPrimaryMode}
                      fontWeight="600"
                      mr={4}
                    >
                      {t("serviceGroup.createServiceGroup")}
                    </Heading>
                    {/* {status} */}
                  </Flex>
                </Flex>

                <Flex align="center" justify="flex-end" gap="1rem">
                  {!isMobile && (
                    <Tooltip
                      p="0.5rem 1rem"
                      borderRadius="1rem"
                      label={
                        <Flex
                          direction="column"
                          justify="center"
                          align="center"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="500"
                            textAlign="center"
                          >
                            {t("serviceGroup.newServiceGroupInfo")}
                          </Text>
                        </Flex>
                      }
                    >
                      <Flex align="center">
                        <Icon
                          as={FaCircleInfo}
                          fontSize="0.875rem"
                          color={primaryAlt}
                        />
                      </Flex>
                    </Tooltip>
                  )}

                  <StatusButtonNew
                    isSmall
                    currentStatus={status}
                    options="serviceNew"
                    onUpdateStatus={setStatus}
                    size={["sm", "md"]}
                    p={["0 1rem", "0 2rem"]}
                    fontSize={["0.75rem", "0.875rem"]}
                  />
                  {isMobile && (
                    <IconButton
                      position="relative"
                      // top="1.5rem"
                      // right="1.5rem"
                      size="sm"
                      bg={backgroundLight}
                      color={textSecondary}
                      onClick={closeHandler}
                      borderRadius="10rem"
                      _hover={{ bg: primaryAlt, color: white }}
                    >
                      <Icon as={FaXmark} />
                    </IconButton>
                  )}
                </Flex>
              </Flex>
              {/* HEADER END */}
              {/* MAIN START */}
              <Flex
                w="100%"
                h="100%"
                direction="column"
                overflowY="auto"
                p={["1.5rem 0rem 7rem", "1rem 2rem 7rem"]}
              >
                {/* SERVICE DETAILS START */}
                <Flex direction="column" w="100%">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0 0 1rem", "initial"]}
                  >
                    {t("serviceGroup.details")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p={["1.25rem 1rem 1.25rem", "1.25rem 1.5rem 0.25rem"]}
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDefault
                        type="text"
                        isRequired
                        size="lg"
                        label={t("form.serviceGroupName")}
                        labelFontSize="0.8rem"
                        value={name}
                        placeholder={t("placeholder.serviceGroupName")}
                        onChange={(e) => {
                          setName(e.target.value);
                        }}
                        w="100%"
                        errorMessage={t("formError.serviceGroupNameRequired")}
                      />
                    </Flex>
                  </Flex>
                </Flex>
                {/* SERVICE DETAILS END */}

                {/* MAIN END */}

                {/* BOTTOM START */}
                <Flex
                  align="center"
                  justify="flex-end"
                  position={["relative", "fixed"]}
                  bottom={["initial", "2rem"]}
                  right={["initial", "2rem"]}
                  gap="0.5rem"
                  zIndex={9000}
                  p={["1.5rem 1.5rem", "initial"]}
                >
                  <Tooltip
                    bg={buttonRed}
                    color={buttonRedText}
                    padding="0.5rem 1rem"
                    borderRadius="1rem"
                    placement="top"
                    label={
                      tooltipMessages.length > 0 ? (
                        <Flex
                          direction="column"
                          justify="center"
                          align="center"
                        >
                          {tooltipMessages.map((message, index) => (
                            <Text
                              key={index}
                              color={buttonRedText}
                              fontSize="xs"
                              fontWeight="500"
                              textAlign="center"
                            >
                              {message}
                            </Text>
                          ))}
                        </Flex>
                      ) : null
                    }
                    isDisabled={tooltipMessages.length === 0} // Disable tooltip if no errors
                  >
                    <Button
                      type="submit"
                      minWidth="10rem"
                      bg={textPrimaryMode}
                      size="lg"
                      color={whiteMode}
                      fontSize="sm"
                      borderRadius="10rem"
                      border={`1px solid ${whiteMode}`}
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: `1px solid ${whiteMode}`,
                      }}
                      onClick={submitHandler}
                      isDisabled={tooltipMessages.length > 0} // Disable if any errors exist
                    >
                      {t("serviceGroup.createServiceGroup")}
                    </Button>
                  </Tooltip>
                </Flex>
              </Flex>
              {/* BOTTOM END */}
            </Flex>
          </form>
        </Box>
      )}
    </>
  );
};

export default CreateServiceGroup;
